/**
 * Interaksi rangkaian peranti: kunci, log masuk, pairing, snapshot, push.
 *
 * Semua fungsi menerima `fetchImpl` supaya boleh diuji tanpa pelayan sebenar, dan tiada satu pun
 * bergantung pada keadaan global.
 */

import { base64UrlToBytes, bytesToBase64Url, STORES } from './idb.js';
import type { PosStore, CatalogSnapshot } from './store.js';
import type { DeviceIdentity, StoredKeys } from './types.js';

/** Awalan DER PKCS#8 untuk kunci persendirian Ed25519 mentah 32 bait. */
const PKCS8_PREFIX = Uint8Array.from([
  0x30, 0x2e, 0x02, 0x01, 0x00, 0x30, 0x05, 0x06, 0x03, 0x2b, 0x65, 0x70, 0x04, 0x22, 0x04, 0x20,
]);


export class ApiFailure extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiFailure';
  }
}

// ------------------------------------------------------------------ kunci

/**
 * Jana kunci peranti.
 *
 * Keutamaan: kunci **tidak boleh dieksport** (CryptoKey tersimpan dalam IndexedDB). Kunci seperti
 * ini tidak boleh disalin keluar dari PC — DevTools boleh *menggunakannya* di PC itu, tetapi tidak
 * boleh membawanya ke mesin lain. Itu menutup ancaman "salin kunci, cipta jualan palsu dari jauh".
 *
 * Jika pelayar tidak menyokong Ed25519 (atau konteks tidak selamat), ia jatuh balik kepada kunci
 * PKCS8 yang boleh dieksport dan peranti ditanda `exportable` supaya pemilik boleh melihatnya.
 */
export async function generateKeys(): Promise<StoredKeys> {
  try {
    const pair = await crypto.subtle.generateKey({ name: 'Ed25519' }, false, ['sign', 'verify']);
    if (pair.privateKey.extractable !== false) throw new Error('kunci masih boleh dieksport');
    const publicRaw = new Uint8Array(await crypto.subtle.exportKey('raw', pair.publicKey));
    return {
      publicKey: bytesToBase64Url(publicRaw),
      privateKey: pair.privateKey,
      kind: 'webcrypto',
    };
  } catch {
    const pair = await crypto.subtle.generateKey({ name: 'Ed25519' }, true, ['sign', 'verify']);
    const publicRaw = new Uint8Array(await crypto.subtle.exportKey('raw', pair.publicKey));
    const privatePkcs8 = new Uint8Array(await crypto.subtle.exportKey('pkcs8', pair.privateKey));
    return {
      publicKey: bytesToBase64Url(publicRaw),
      privateKey: bytesToBase64Url(privatePkcs8),
      kind: 'exportable',
    };
  }
}

/** Tandatangan `${timestamp}.${rawBody}` — sama seperti yang disahkan pelayan. */
export async function signPayload(
  privateKey: string | CryptoKey,
  timestamp: number,
  rawBody: string,
): Promise<string> {
  const message = new TextEncoder().encode(`${timestamp}.${rawBody}`);

  // Kunci tidak boleh dieksport: tandatangan dibuat oleh pelayar; kunci tidak pernah keluar.
  if (typeof privateKey !== 'string') {
    const signature = await crypto.subtle.sign({ name: 'Ed25519' }, privateKey, message);
    return bytesToBase64Url(new Uint8Array(signature));
  }

  const key = await crypto.subtle.importKey(
    'pkcs8',
    base64UrlToBytes(privateKey),
    { name: 'Ed25519' },
    false,
    ['sign'],
  );
  const signature = await crypto.subtle.sign({ name: 'Ed25519' }, key, message);
  return bytesToBase64Url(new Uint8Array(signature));
}


/** Kunci persendirian mentah daripada PKCS#8, untuk alat diagnostik. */
export function privateKeySeed(privateKeyBase64Url: string): Uint8Array {
  const pkcs8 = base64UrlToBytes(privateKeyBase64Url);
  if (pkcs8.length !== PKCS8_PREFIX.length + 32) {
    throw new Error('kunci persendirian bukan PKCS#8 Ed25519 yang dijangka');
  }
  return pkcs8.subarray(PKCS8_PREFIX.length);
}

// ------------------------------------------------------------------- HTTP

async function readError(response: Response): Promise<ApiFailure> {
  let code = `HTTP_${response.status}`;
  let message = response.statusText || 'permintaan gagal';
  try {
    const body = (await response.json()) as { error?: { code?: string; message?: string } };
    code = body.error?.code ?? code;
    message = body.error?.message ?? message;
  } catch {
    // Bukan JSON; kekalkan mesej lalai.
  }
  return new ApiFailure(response.status, code, message);
}

export interface LoginResult {
  token: string;
  tenant: { id: string; name: string; slug: string };
  outlets: Array<{ id: string; code: string }>;
}

export async function login(
  apiBase: string,
  credentials: { email: string; password: string; shopSlug: string },
  fetchImpl: typeof fetch = fetch,
): Promise<LoginResult> {
  const response = await fetchImpl(`${apiBase}/v1/auth/login`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      email: credentials.email,
      password: credentials.password,
      shop_slug: credentials.shopSlug,
    }),
  });
  if (!response.ok) throw await readError(response);

  // Disahkan, bukan di-cast. `as LoginResult` pernah menyembunyikan percanggahan nama medan
  // (`access_token` vs `token`) yang menyebabkan `Bearer undefined` dihantar ke pelayan.
  const body: unknown = await response.json();
  if (
    typeof body !== 'object' ||
    body === null ||
    typeof (body as { access_token?: unknown }).access_token !== 'string'
  ) {
    throw new ApiFailure(500, 'BAD_LOGIN_RESPONSE', 'Respons log masuk tidak seperti yang dijangka');
  }
  const parsed = body as {
    access_token: string;
    tenant: LoginResult['tenant'];
    outlets: LoginResult['outlets'];
  };
  return { token: parsed.access_token, tenant: parsed.tenant, outlets: parsed.outlets };
}

export interface PairResult {
  identity: DeviceIdentity;
  /** Peranti mesti diluluskan pengurus sebelum boleh berjual. */
  requiresApproval: boolean;
  status: 'pending' | 'active';
  /** `client_seq` tertinggi di cawangan ini — penomboran resit diteruskan dari sini. */
  lastClientSeq: number;
}

export async function pairDevice(
  apiBase: string,
  input: {
    code: string;
    publicKey: string;
    agentVersion: string;
    /** 'webcrypto' = kunci tidak boleh dieksport; 'exportable' = kunci lama. */
    keyProtection?: 'webcrypto' | 'exportable';
  },
  fetchImpl: typeof fetch = fetch,
): Promise<PairResult> {
  const response = await fetchImpl(`${apiBase}/v1/devices/pair`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      code: input.code.trim().toUpperCase(),
      public_key: input.publicKey,
      agent_version: input.agentVersion,
      ...(input.keyProtection === undefined ? {} : { key_protection: input.keyProtection }),
    }),
  });
  if (!response.ok) throw await readError(response);
  const body = (await response.json()) as {
    device_id: string;
    tenant_id: string;
    tenant_name?: string;
    outlet_id: string;
    outlet_code: string;
    device_no: number;
    requires_approval?: boolean;
    status?: string;
    last_client_seq?: number;
  };
  const identity: DeviceIdentity = {
    deviceId: body.device_id,
    tenantId: body.tenant_id,
    outletId: body.outlet_id,
    outletCode: body.outlet_code,
    deviceNo: body.device_no,
  };
  // `exactOptionalPropertyTypes`: medan pilihan tidak boleh diberi `undefined` secara eksplisit.
  if (typeof body.tenant_name === 'string' && body.tenant_name !== '') {
    identity.tenantName = body.tenant_name;
  }

  return {
    identity,
    requiresApproval: body.requires_approval !== false,
    status: body.status === 'active' ? 'active' : 'pending',
    lastClientSeq: Number(body.last_client_seq ?? 0),
  };
}

/**
 * Snapshot katalog menggunakan tandatangan PERANTI, bukan sesi pemilik.
 *
 * Inilah yang membolehkan PC kaunter dipasangkan dengan kod pairing sahaja — kata laluan pemilik
 * tidak pernah menyentuh PC kasir. Laporan kewangan tetap memerlukan sesi pemilik.
 */
export async function fetchSnapshotWithDevice(
  apiBase: string,
  deviceId: string,
  privateKey: string | CryptoKey,
  fetchImpl: typeof fetch = fetch,
): Promise<CatalogSnapshot> {
  const timestamp = Math.floor(Date.now() / 1000);
  const signature = await signPayload(privateKey, timestamp, '');

  const response = await fetchImpl(`${apiBase}/v1/sync/catalog/snapshot`, {
    headers: {
      'x-device-id': deviceId,
      'x-device-timestamp': String(timestamp),
      'x-device-signature': signature,
    },
  });
  if (!response.ok) throw await readError(response);
  return (await response.json()) as CatalogSnapshot;
}

export async function fetchSnapshot(
  apiBase: string,
  token: string,
  fetchImpl: typeof fetch = fetch,
): Promise<CatalogSnapshot> {
  const response = await fetchImpl(`${apiBase}/v1/sync/catalog/snapshot`, {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw await readError(response);
  return (await response.json()) as CatalogSnapshot;
}

// ------------------------------------------------------------------- push

export interface PushRejection {
  client_seq: number;
  code: string;
  message: string;
  retryable: boolean;
}

export interface PushOutcome {
  /** Kod ralat apabila permintaan ditolak sepenuhnya (contoh DEVICE_REVOKED). */
  errorCode?: string;
  errorMessage?: string;
  status: number;
  accepted: number[];
  rejected: PushRejection[];
  duplicateBatch: boolean;
}

export interface PushOptions {
  store: PosStore;
  apiBase: string;
  deviceId: string;
  privateKey: string | CryptoKey;
  fetchImpl?: typeof fetch;
  now?: () => number;
  newBatchId?: () => string;
}

/**
 * Hantar semua entri outbox yang tertunggak.
 *
 * Pulangkan `null` apabila tiada apa-apa untuk dihantar — pemanggil tidak perlu memanggil
 * rangkaian secara sia-sia.
 */
export async function pushPending(options: PushOptions): Promise<PushOutcome | null> {
  const { store, apiBase, deviceId, privateKey } = options;
  const fetchImpl = options.fetchImpl ?? fetch;
  const now = options.now ?? Date.now;
  const batchId = (options.newBatchId ?? (() => crypto.randomUUID()))();

  const entries = await store.pendingOutbox();
  if (entries.length === 0) return null;

  const body = {
    batch_id: batchId,
    sent_at: new Date(now()).toISOString(),
    records: entries.map((entry) => ({
      client_seq: entry.client_seq,
      type: entry.type,
      payload: entry.payload,
    })),
  };
  const raw = JSON.stringify(body);
  const timestamp = Math.floor(now() / 1000);
  const signature = await signPayload(privateKey, timestamp, raw);

  let response: Response;
  try {
    response = await fetchImpl(`${apiBase}/v1/sync/push`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'x-device-id': deviceId,
        'x-device-timestamp': String(timestamp),
        'x-device-signature': signature,
      },
      body: raw,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : 'rangkaian gagal';
    await store.recordAttempt(
      entries.map((entry) => entry.client_seq),
      message,
    );
    return { status: 0, accepted: [], rejected: [], duplicateBatch: false };
  }

  if (!response.ok) {
    const failure = await readError(response);
    await store.recordAttempt(
      entries.map((entry) => entry.client_seq),
      `${failure.code}: ${failure.message}`,
    );
    return {
      status: response.status,
      accepted: [],
      rejected: [],
      duplicateBatch: false,
      // Kod ralat diteruskan kepada pemanggil: POS mesti tahu jika peranti sudah DICABUT, bukan
      // sekadar "sync gagal". Perbezaan itu menentukan sama ada kasir boleh terus menerima wang.
      errorCode: failure.code,
      errorMessage: failure.message,
    };
  }

  const result = (await response.json()) as {
    accepted: number[];
    rejected: PushRejection[];
    duplicate_batch?: boolean;
  };

  if (result.accepted.length > 0) await store.markSynced(result.accepted);
  if (result.rejected.length > 0) {
    await store.markRejected(
      result.rejected.map((rejection) => rejection.client_seq),
      result.rejected.map((rejection) => rejection.code).join(', '),
    );
  }

  return {
    status: response.status,
    accepted: result.accepted,
    rejected: result.rejected,
    duplicateBatch: result.duplicate_batch === true,
  };
}

export { STORES };

/**
 * Status peranti mengikut pelayan.
 *
 * Ditandatangani dengan kunci peranti. POS menggunakannya SEBELUM menerima wang, supaya peranti
 * yang dicabut tidak boleh terus berjual tanpa rekod.
 */
export async function deviceStatus(
  apiBase: string,
  deviceId: string,
  privateKey: string | CryptoKey,
  fetchImpl: typeof fetch = fetch,
): Promise<{
  status: string;
  deviceNo: number;
  outletCode: string;
  /** Cukai jualan dihidupkan oleh pemilik? Disemak setiap kali POS dibuka. */
  taxEnabled?: boolean;
  taxInclusive?: boolean;
  /** Kepala dan footer resit daripada tetapan kedai. */
  receiptHeader?: string[];
  receiptFooter?: string;
} | null> {
  const timestamp = Math.floor(Date.now() / 1000);
  // GET tiada badan; pelayan menandatangani `${timestamp}.` untuk permintaan kosong.
  const signature = await signPayload(privateKey, timestamp, '');

  try {
    const response = await fetchImpl(`${apiBase}/v1/sync/device`, {
      headers: {
        'x-device-id': deviceId,
        'x-device-timestamp': String(timestamp),
        'x-device-signature': signature,
      },
    });
    if (!response.ok) return null;
    const body = (await response.json()) as {
      status: string;
      device_no: number;
      outlet_code: string;
      tax_enabled?: boolean;
      tax_inclusive?: boolean;
      receipt_header?: string[];
      receipt_footer?: string;
    };
    return {
      status: body.status,
      deviceNo: body.device_no,
      outletCode: body.outlet_code,
      taxEnabled: body.tax_enabled === true,
      taxInclusive: body.tax_inclusive === true,
      receiptHeader: Array.isArray(body.receipt_header) ? body.receipt_header : [],
      receiptFooter: typeof body.receipt_footer === 'string' ? body.receipt_footer : '',
    };
  } catch {
    return null;
  }
}